'use client'

import { useEffect, useRef } from 'react'
import type { MembershipsInfo } from '@/lib/api'
import { useMembershipsStore } from '@/store/membershipsStore'
import { useAuth } from './useAuth'

interface UseMembershipsResult {
  memberships: MembershipsInfo | null
  isLoading: boolean
  error: Error | null
  refresh: () => Promise<void>
}

/**
 * Fetches the current user's cheap membership/role data without touching billing
 * provider reconciliation.
 */
/** The account whose anonymous→signed-in transition already refetched memberships. */
let transitionFetchedFor: string | null = null

export function useMemberships(): UseMembershipsResult {
  const { isAnonymous, isLoading: authLoading, userId } = useAuth()
  const memberships = useMembershipsStore(s => s.memberships)
  const isLoading = useMembershipsStore(s => s.isLoading)
  const error = useMembershipsStore(s => s.error)
  const fetch = useMembershipsStore(s => s.fetch)
  const forceFetch = useMembershipsStore(s => s.forceFetch)
  const clear = useMembershipsStore(s => s.clear)
  const setReady = useMembershipsStore(s => s.setReady)
  const prevIsAnonymous = useRef(isAnonymous)

  useEffect(() => {
    if (authLoading) return
    if (isAnonymous) {
      clear()
      setReady()
      return
    }
    fetch()
  }, [authLoading, isAnonymous, fetch, clear, setReady])

  useEffect(() => {
    // Every mounted consumer sees the sign-in; one refetch per account is enough (each forced fetch
    // supersedes the last, so one per consumer meant one request per consumer).
    if (prevIsAnonymous.current && !isAnonymous && !authLoading && transitionFetchedFor !== userId) {
      transitionFetchedFor = userId
      useMembershipsStore.setState({ isLoading: true })
      forceFetch()
    }
    prevIsAnonymous.current = isAnonymous
  }, [isAnonymous, authLoading, userId, forceFetch])

  useEffect(() => {
    const handleFocus = () => {
      if (!isAnonymous) fetch()
    }
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [fetch, isAnonymous])

  return {
    memberships,
    isLoading,
    error,
    refresh: forceFetch,
  }
}
